import { Box } from '@mui/material';
import React from 'react';
import ReactSelect, { SingleValue } from 'react-select';
import { translate as t } from 'hooks/useTranslations';
import dayjs, { Dayjs } from 'dayjs';

export interface PeriodOption<T = string> {
  value: T;
  label: string;
}

export interface ReportingPeriodPickerProps<T = string> {
  /** Whether to hide the picker */
  hidePicker?: boolean;
  /** Current selected value */
  value?: PeriodOption<T>;
  /** Available options */
  options: PeriodOption<T>[];
  /** Callback when selection changes */
  onChange: (value: SingleValue<PeriodOption<T>>) => void;
  /** Translation key for the field name */
  nameKey: string;
  /** Translation key for the placeholder */
  placeholderKey: string;
  /** Size of the select component */
  size?: string;
  /** Additional CSS classes or styles */
  className?: string;
  /** Additional styling */
  sx?: object;
}

/**
 * A reusable period picker component for reporting modules.
 * Provides a standardized way to select time periods across different reporting views.
 */
export function ReportingPeriodPicker<T = string>({
  hidePicker = false,
  value,
  options,
  onChange,
  nameKey,
  placeholderKey,
  size,
  className,
  sx,
}: ReportingPeriodPickerProps<T>) {
  if (hidePicker) {
    return null;
  }

  return (
    <Box
      sx={{ display: 'flex', gap: '16px', ...sx }}
      className={className}
    >
      <ReactSelect
        isMulti={false}
        options={options}
        value={value}
        name={t(nameKey)}
        placeholder={t(placeholderKey)}
        menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
        menuPosition='fixed'
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          input: (base) => ({
            ...base,
            boxShadow: 'none',
            outline: 0,
            minHeight: size === 'small' ? 26 : 45,
          }),
        }}
        formatOptionLabel={(option, { context }) => {
          if (context === 'value') {
            return `${t(nameKey)}: ${option.label}`;
          }
          return option.label;
        }}
        onChange={onChange}
      />
    </Box>
  );
}

export default ReportingPeriodPicker;

/**
 * Date range result interface
 */
export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Date range calculation configuration
 */
export interface DateRangeConfig {
  /** The period value to calculate dates for */
  period: string;
  /** Custom date calculation function */
  calculateDates: (period: string) => DateRange;
}

/**
 * Utility function to get start and end dates from a period picker value.
 * This function provides a generic way to convert period values to date ranges.
 *
 * @param periodValue - The period value from the picker
 * @param config - Configuration object with the period and calculation function
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 *
 * @example
 * ```typescript
 * const dates = getDateRangeFromPeriod('past30Days', {
 *   period: 'past30Days',
 *   calculateDates: (period) => {
 *     const today = dayjs();
 *     const startDate = today.subtract(30, 'day');
 *     return {
 *       startDate: startDate.format('YYYY-MM-DD'),
 *       endDate: today.format('YYYY-MM-DD')
 *     };
 *   }
 * });
 * ```
 */
export function getDateRangeFromPeriod(periodValue: string, config: DateRangeConfig): DateRange {
  return config.calculateDates(periodValue);
}

/**
 * Pre-configured date range calculator for common period types.
 * This provides a standardized way to calculate dates for common reporting periods.
 *
 * @param period - The period type
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export function calculateStandardDateRange(period: string): DateRange {
  const today = dayjs();
  let startDate: Dayjs;
  let endDate: Dayjs = today;

  switch (period) {
    case 'past30Days':
      startDate = today.subtract(30, 'day');
      break;
    case 'previousMonth':
      startDate = today.subtract(1, 'month').startOf('month');
      endDate = today.subtract(1, 'month').endOf('month');
      break;
    case 'past3Months':
      startDate = today.subtract(3, 'month');
      break;
    case 'yearToDate':
      startDate = today.startOf('year');
      endDate = today.subtract(1, 'month').endOf('month');
      break;
    case 'past12Months':
      startDate = today.subtract(12, 'month');
      break;
    case 'lastCalendarYear':
      startDate = today.subtract(1, 'year').startOf('year');
      endDate = today.subtract(1, 'year').endOf('year');
      break;
    default:
      startDate = today;
      break;
  }

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  };
}

/**
 * Generate time filter options from an enum.
 * Creates options array for use with ReportingPeriodPicker.
 *
 * @param enumObject - The enum object to generate options from
 * @param translationKeyPrefix - Translation key prefix for the options
 * @returns Array of PeriodOption objects
 *
 * @example
 * ```typescript
 * enum DateRangeFilter {
 *   PAST_30_DAYS = 'past30Days',
 *   PAST_3_MONTHS = 'past3Months'
 * }
 *
 * const options = getTimeFilterOptions(DateRangeFilter, 'reporting.filters.date.options');
 * ```
 */
export function getTimeFilterOptions<T extends Record<string, string>>(
  enumObject: T,
  translationKeyPrefix: string
): PeriodOption<T[keyof T]>[] {
  return Object.values(enumObject).map((enumValue) => {
    return {
      value: enumValue as T[keyof T],
      label: t(`${translationKeyPrefix}.${enumValue}`),
    };
  });
}

/**
 * Find a specific filter option by its value.
 *
 * @param value - The enum value to find
 * @param enumObject - The enum object
 * @param translationKeyPrefix - Translation key prefix for the options
 * @returns The matching PeriodOption or undefined
 */
export function getDateFilterValue<T extends Record<string, string>>(
  value: T[keyof T],
  enumObject: T,
  translationKeyPrefix: string
): PeriodOption<T[keyof T]> | undefined {
  return getTimeFilterOptions(enumObject, translationKeyPrefix).find((option) => option.value === value);
}

/**
 * Get start and end dates from a filter value.
 * Uses the centralized date calculation logic.
 *
 * @param filter - The filter value
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 *
 * @example
 * ```typescript
 * const dates = getDateRangeFromFilter('past30Days');
 * // Returns: { startDate: '2024-01-01', endDate: '2024-01-31' }
 * ```
 */
export function getDateRangeFromFilter(filter: string): DateRange {
  return calculateStandardDateRange(filter);
}
